import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Select from 'react-select';
import '../priceOverridePanel.css';

/** The override-kind choices, react-select shaped (2026-08-03: the
 *  portal uses react-select everywhere — no native selects). */
const OVERRIDE_KIND_OPTIONS = [
    { value: '', label: 'List price' },
    { value: '%', label: '% off' },
    { value: 'aed', label: 'AED rate' },
];
const overrideKindStyles = {
    control: (base, state) => ({
        ...base, minHeight: 36, height: 36, borderRadius: 8,
        borderColor: state.isFocused ? '#ff5a60' : '#e3e3e8',
        boxShadow: 'none', fontSize: 13, cursor: 'pointer',
        ':hover': { borderColor: '#c9c9d2' },
    }),
    valueContainer: (base) => ({ ...base, height: 36, padding: '0 10px' }),
    indicatorsContainer: (base) => ({ ...base, height: 36 }),
    dropdownIndicator: (base) => ({ ...base, padding: '0 8px', color: '#9a9aa4' }),
    indicatorSeparator: () => ({ display: 'none' }),
    menu: (base) => ({ ...base, fontSize: 13, borderRadius: 8, overflow: 'hidden' }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#ff5a60' : state.isFocused ? '#fdecec' : '#fff',
        color: state.isSelected ? '#fff' : '#333',
        cursor: 'pointer',
    }),
    // The table scrolls (overflow-x:auto), which also CLIPS anything that
    // hangs below a row — the owner's screenshot showed the dropdown cut in half.
    // Portaling the menu to <body> takes it out of every clipping context,
    // including the edit-booking modal.
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Item IDs matching the DB trigger
const ITEMS = {
    HOURLY_WITH: 67, HOURLY_WITHOUT: 68, DEEP_CLEANING: 66,
    DELIVERY_PICKUP: 84, MINIMUM_CALLOUT: 81,
    CHECKIN_3_7: 223, CHECKIN_OTHER: 217,
    AMENITY_PACK: 21, CANCELLATION_FEE: 72,
};
const TYPES = {
    ALL_INCLUSIVE: 1, HOURLY_WITH: 2, HOURLY_WITHOUT: 3, DEEP_CLEANING: 4,
    ALL_INCLUSIVE_DEEP: 5, LAUNDRY_ONLY: 6, DELIVERY: 7, PICKUP: 8,
    TOUCHUP: 9, MINIMUM_CALLOUT: 10, CHECKIN: 11, PORTER: 12,
};

export default function BookingPricePreview({
    selectedAddons, clientId, propertyId, properties,
    bookingId, typeId, statusId, bookingStart, bookingEnd,
    requiredCleaners, isAmenities, duration,
    // Maintenance: when isMaintenance is true, the main line is a single
    // "Maintenance Service" entry with rate = customPriceExclVat (net of
    // VAT). Add-ons still apply on top. All other type-based logic is
    // skipped.
    isMaintenance = false, customPriceExclVat = null,
    // The main line's name for a maintenance-like type ("Swimming Pool
    // Maintenance Service" for the pool type) — what the invoice will show.
    maintenanceLabel = 'Maintenance Service',
    // When the backend will auto-add the one-time linen deposit (first
    // linen-hire booking of the property), the preview must show it — the
    // wallet is charged the REAL total, so the preview cannot understate it.
    autoLinenDepositBeds = 0,
    autoLinenDepositSkBeds = 0,
    // Reports the live grand total (incl. VAT) up to the parent so the admin
    // payment-mode selector can pre-select wallet-vs-card by coverage. Pass a
    // stable setter (e.g. a useState setter) to avoid effect churn.
    onTotal = null,
    // Called with false while item prices load, true once the total is real.
    onReady = null,
    // Admin add-booking moves the wallet balance/coverage into the payment
    // selector (shown only when "wallet" is chosen), so the preview hides its
    // own wallet block. Defaults true to keep every other usage unchanged.
    showWallet = true,
    /**
     * PRICE OVERRIDES, IN THIS TABLE (2026-08-03). A manager presses ✎ Edit prices and
     * the rows themselves grow the controls; the Previously-Charged / Refund
     * block below then answers the money question in the same breath.
     *
     * `overrides` is item_id -> { kind: ''|'%'|'aed', value, reason }, owned
     * by the parent because the two forms consume it differently: AddBooking
     * sends it with the create; EditBooking applies it at once through
     * onApplyOverrides (the PUT that settles refunds).
     */
    canOverride = false,
    overrides = null,
    onOverridesChange = null,
    onApplyOverrides = null,
    /**
     * QUANTITY, IN THE SAME PANEL (2026-08-30 — "at edit booking edit prices can
     * we just edit quantity too please"). Add-on quantities already live in the
     * form's Add-ons block; editing a price meant scrolling back up to change a
     * count. Called with (itemId, qty) so the parent keeps owning the state —
     * the preview never holds booking data of its own.
     * Omit it and the quantity column stays read-only, exactly as before.
     */
    onQuantityChange = null
}) {
    const [editingPrices, setEditingPrices] = useState(false);
    const [applyMsg, setApplyMsg] = useState('');
    // Bumped when the settled figure needs re-reading. Nothing in this panel
    // settles money any more (the booking's own Save does), so it only ever
    // holds its initial value — kept as the fetch's dependency so the hook
    // below reads the same way it always has.
    const [settledTick] = useState(0);
    // Snapshot taken when edit mode opens, so Cancel can put things back
    // exactly as they were (2026-08-03: Done applies, Cancel discards).
    const [draftBackup, setDraftBackup] = useState(null);
    const [wallet, setWallet] = useState(null);
    const [walletLoading, setWalletLoading] = useState(false);
    const [itemsCache, setItemsCache] = useState({});
    const [itemsLoading, setItemsLoading] = useState(true);
    const [originalTotal, setOriginalTotal] = useState(0); // For edit mode: what was previously charged

    const property = useMemo(() => {
        if (!propertyId || !properties) return null;
        return properties.find(p => p.id === parseInt(propertyId) || p.id === propertyId) || null;
    }, [propertyId, properties]);

    // Fetch items needed for pricing
    useEffect(() => {
        const needed = new Set(Object.values(ITEMS));
        if (property?.item_id) needed.add(property.item_id);
        if (property?.off_peak_item_id) needed.add(property.off_peak_item_id);
        const missing = [...needed].filter(id => !itemsCache[id]);
        if (missing.length === 0) { setItemsLoading(false); return; }
        let cancelled = false;
        setItemsLoading(true);
        (async () => {
            const results = {};
            for (const id of missing) {
                try { const res = await axios.get(`/api/items/${id}`); results[id] = res.data; } catch { /* skip */ }
            }
            if (!cancelled) {
                setItemsCache(prev => ({ ...prev, ...results }));
                setItemsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [property?.item_id, property?.off_peak_item_id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch wallet balance
    useEffect(() => {
        if (!clientId) { setWallet(null); return; }
        let cancelled = false;
        (async () => {
            setWalletLoading(true);
            try { const res = await axios.get(`/api/wallet/clients/${clientId}`); if (!cancelled) setWallet(res.data.wallet); }
            catch { if (!cancelled) setWallet(null); }
            finally { if (!cancelled) setWalletLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [clientId]);

    // Edit mode: the TRUE amount already settled for this booking. Must come from
    // the raw ledger (this admin endpoint), NOT /wallet/clients/:id/transactions —
    // that endpoint hides card round-trips, so a card-paid booking would read as
    // "charged 0" and the price difference would be wrong (payments Phase 3).
    //
    // DIRECT CARD RAIL (2026-07-18). `net_charged` counts WALLET rows only, and a
    // direct-rail card booking has none — it writes booking_payment_receipts
    // instead. Reading net_charged therefore printed "Previously Charged AED 0.00"
    // (or suppressed the row entirely, which is worse: the whole booking total then
    // reads as still-to-charge) for a booking that was paid in full. `settled_total`
    // is the server's wallet-net + receipts − refunds figure; fall back to
    // net_charged only for a server that predates it.
    useEffect(() => {
        if (!bookingId) { setOriginalTotal(0); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`/api/bookings/admin/net-charged/${bookingId}`);
                const settled = Number(res.data?.settled_total);
                const value = Number.isFinite(settled) ? settled : parseFloat(res.data?.net_charged || 0);
                if (!cancelled) setOriginalTotal(value);
            } catch { if (!cancelled) setOriginalTotal(0); }
        })();
        return () => { cancelled = true; };
    }, [bookingId, settledTick]);

    const getItem = (id) => itemsCache[id];

    // 9.00 -> "9", 4.50 -> "4.5". Quantities are NUMERIC, and the trailing
    // zeros make a whole-number quantity look like a price. Named fmtQty, not
    // qty: buildLine already takes a `qty` parameter.
    const fmtQty = (n) => String(Number(n ?? 0)).replace(/\.0+$/, '');

    const buildLine = (name, qty, unitPrice, discount = 0, tag = null, itemId = null, addonId = null) => {
        const isLHD = (name || '').toLowerCase().includes('linen hire deposit');
        // The override, applied EXACTLY like the v2 trigger: a percent
        // resolves to a rate rounded to fils first (never a Zoho discount —
        // that field belongs to the wallet bonus system), an AED value IS the
        // rate, and the deposit is untouchable. Off-peak's 10% falls away
        // when a manual override lands: manual replaces automatic.
        const o = overrides && itemId != null && !isLHD ? overrides[itemId] : null;
        let effUnit = unitPrice;
        let effDiscount = discount;
        let overridden = false;
        if (o && o.kind === '%' && o.value !== '' && o.value != null) {
            const pct = Math.min(100, Math.max(0, parseFloat(o.value) || 0));
            effUnit = Math.round(unitPrice * (1 - pct / 100) * 100) / 100;
            effDiscount = 0;
            overridden = true;
        } else if (o && o.kind === 'aed' && o.value !== '' && o.value != null) {
            effUnit = Math.max(0, parseFloat(o.value) || 0);
            effDiscount = 0;
            overridden = true;
        }
        // A manually set quantity replaces the computed one everywhere the
        // preview adds up — otherwise the row would show 8 hours and total 9.
        const effQty = o && o.qty !== '' && o.qty != null && parseFloat(o.qty) > 0
            ? parseFloat(o.qty)
            : qty;
        const sub = effQty * effUnit * (1 - effDiscount / 100);
        const taxRate = isLHD ? 0 : 0.05;
        const tax = sub * taxRate;
        return {
            name: overridden ? `${name} - Custom Price` : name,
            quantity: effQty, unitPrice: effUnit, discount: effDiscount,
            quantityOverridden: effQty !== qty,
            // What it would have been — the strike-through value.
            computedQuantity: qty,
            lineSubtotal: sub, taxRate, taxAmount: tax, lineTotal: sub + tax,
            tag: overridden ? 'Custom' : tag,
            itemId, overridden, originalUnit: unitPrice, lockedDeposit: isLHD,
            // Only ADD-ON lines carry this: they are the ones whose quantity is
            // a real number a manager chose, rather than derived from the
            // booking (a cleaning is one cleaning; bedrooms come from the unit).
            addonId,
        };
    };

    // Off-peak check
    const isOffPeak = useMemo(() => {
        if (!bookingStart || !bookingEnd) return false;
        const start = bookingStart instanceof Date ? bookingStart : new Date(bookingStart);
        const end = bookingEnd instanceof Date ? bookingEnd : new Date(bookingEnd);
        return start.getHours() >= 15 || end.getHours() < 11 || (end.getHours() === 11 && end.getMinutes() < 30);
    }, [bookingStart, bookingEnd]);

    // Duration in hours
    const durationHours = useMemo(() => {
        if (duration) return parseFloat(duration);
        if (!bookingStart || !bookingEnd) return 0;
        const s = bookingStart instanceof Date ? bookingStart : new Date(bookingStart);
        const e = bookingEnd instanceof Date ? bookingEnd : new Date(bookingEnd);
        return (e - s) / 3600000;
    }, [bookingStart, bookingEnd, duration]);

    const parsedType = parseInt(typeId) || 0;
    const parsedStatus = parseInt(statusId) || 0;

    // Calculate price — same logic for both add and edit
    const display = useMemo(() => {
        const items = [];

        // Last Minute Cancelled/Rescheduled
        if (parsedStatus === 5 || parsedStatus === 6) {
            const ci = getItem(ITEMS.CANCELLATION_FEE);
            if (ci) items.push(buildLine(ci.name, 1, parseFloat(ci.default_price), 0, 'Penalty', ITEMS.CANCELLATION_FEE));
            if (items.length === 0) return null;
            const sub = items.reduce((s, i) => s + i.lineSubtotal, 0);
            const vat = items.reduce((s, i) => s + i.taxAmount, 0);
            return { items, subtotal: +sub.toFixed(2), vat: +vat.toFixed(2), total: +(sub + vat).toFixed(2) };
        }

        // Maintenance: admin-entered net price replaces all type-based line
        // logic. Mirrors the SQL trigger's Maintenance branch.
        if (isMaintenance) {
            const price = parseFloat(customPriceExclVat) || 0;
            if (price > 0) {
                items.push(buildLine(maintenanceLabel, 1, price, 0, 'Service', null));
            }
        } else {
            // Main item by type
            if (parsedType === TYPES.DELIVERY || parsedType === TYPES.PICKUP) {
                const it = getItem(ITEMS.DELIVERY_PICKUP);
                if (it) items.push(buildLine(it.name, 1, parseFloat(it.default_price), 0, 'Service', ITEMS.DELIVERY_PICKUP));
            } else if ([TYPES.HOURLY_WITH, TYPES.HOURLY_WITHOUT, TYPES.DEEP_CLEANING].includes(parsedType)) {
                const itemId = parsedType === TYPES.HOURLY_WITH ? ITEMS.HOURLY_WITH : parsedType === TYPES.HOURLY_WITHOUT ? ITEMS.HOURLY_WITHOUT : ITEMS.DEEP_CLEANING;
                const it = getItem(itemId);
                const qty = +(durationHours * (parseInt(requiredCleaners) || 1)).toFixed(2);
                if (it && qty > 0) items.push(buildLine(it.name, qty, parseFloat(it.default_price), 0, 'Service', itemId));
            } else if (parsedType === TYPES.ALL_INCLUSIVE || parsedType === TYPES.ALL_INCLUSIVE_DEEP) {
                if (isOffPeak && property?.off_peak_item_id) {
                    const opItem = getItem(property.off_peak_item_id);
                    if (opItem) items.push(buildLine(opItem.name, 1, parseFloat(opItem.default_price), 0, 'Off-Peak', property.off_peak_item_id));
                } else if (property?.item_id) {
                    const it = getItem(property.item_id);
                    if (it) items.push(buildLine(it.name, 1, parseFloat(it.default_price), isOffPeak ? 10 : 0, isOffPeak ? 'Off-Peak' : 'Service', property.item_id));
                }
            } else if (parsedType === TYPES.CHECKIN) {
                const h = bookingStart instanceof Date ? bookingStart.getHours() : 0;
                const checkinId = (h >= 15 && h < 19) ? ITEMS.CHECKIN_3_7 : ITEMS.CHECKIN_OTHER;
                const it = getItem(checkinId);
                if (it) items.push(buildLine(it.name, 1, parseFloat(it.default_price), 0, 'Service', checkinId));
            } else if (parsedType === TYPES.MINIMUM_CALLOUT) {
                const it = getItem(ITEMS.MINIMUM_CALLOUT);
                if (it) items.push(buildLine(it.name, 1, parseFloat(it.default_price), 0, 'Service', ITEMS.MINIMUM_CALLOUT));
            }
        }

        // Amenities for non-type-1/5 (skip for Maintenance — toggle is hidden)
        if (isAmenities && !isMaintenance && parsedType !== TYPES.ALL_INCLUSIVE && parsedType !== TYPES.ALL_INCLUSIVE_DEEP) {
            const am = getItem(ITEMS.AMENITY_PACK);
            if (am) items.push(buildLine(am.name, Math.max(1, property?.bedrooms || 0), parseFloat(am.default_price), 0, 'Add-on', ITEMS.AMENITY_PACK));
        }

        // Addons
        if (selectedAddons?.length > 0) {
            for (const a of selectedAddons) {
                // A custom add-on already carries its own price: no override
                // control for it (its id is a form-local string, not an item).
                const aid = a.custom ? null : (a.item_id || a.id || null);
                items.push(buildLine(a.name, parseFloat(a.quantity) || 0, parseFloat(a.price) || 0, 0, 'Add-on', aid, aid));
            }
        }

        // Auto linen deposit (100 AED PER BED, VAT-free) — added by the
        // backend on the first linen-hire All-Inclusive booking; shown here so
        // the preview total matches exactly what the wallet will be charged.
        // Skipped when the admin already added item 73 manually (it's in
        // selectedAddons and rendered above).
        const depositAlreadyListed = (selectedAddons || []).some(
            (a) => (a.name || '').toLowerCase().includes('linen hire deposit')
        );
        if ((autoLinenDepositBeds > 0 || autoLinenDepositSkBeds > 0) && !depositAlreadyListed
            && (parsedType === TYPES.ALL_INCLUSIVE || parsedType === TYPES.ALL_INCLUSIVE_DEEP)) {
            if (autoLinenDepositBeds > 0) {
                items.push(buildLine('Linen Hire Deposit (one-time, refundable)', autoLinenDepositBeds, 100, 0, 'Deposit', null));
            }
            if (autoLinenDepositSkBeds > 0) {
                items.push(buildLine('Linen Hire Deposit — Super King (one-time, refundable)', autoLinenDepositSkBeds, 125, 0, 'Deposit', null));
            }
        }

        if (items.length === 0) return null;
        const sub = items.reduce((s, i) => s + i.lineSubtotal, 0);
        const vat = items.reduce((s, i) => s + i.taxAmount, 0);
        return { items, subtotal: +sub.toFixed(2), vat: +vat.toFixed(2), total: +(sub + vat).toFixed(2) };
    }, [parsedType, parsedStatus, property, isOffPeak, durationHours, requiredCleaners, isAmenities, selectedAddons, bookingStart, itemsCache, isMaintenance, customPriceExclVat, autoLinenDepositBeds, autoLinenDepositSkBeds, overrides]); // eslint-disable-line react-hooks/exhaustive-deps

    // Report the live grand total up to the parent (for the payment-mode
    // selector). Fires with 0 when there is no priced line yet.
    //
    // NEVER WHILE THE ITEM PRICES ARE STILL LOADING (2026-08-30). This effect
    // sits above the `if (itemsLoading)` return, so it used to publish whatever
    // `display` could compute from a half-filled itemsCache — an intermediate
    // total. On an already-paid booking the parent turns that into a settlement:
    // the edit dialog showed "Booking Cost Preview: Loading pricing…" and, right
    // underneath, "Refund to wallet — AED 425.25 goes back to the client's
    // wallet", with Update Booking live. The price was 1,156.25; the intermediate
    // was 731.00. Pressing Update there would have refunded money against a
    // number that never existed.
    //
    // `onReady` lets the parent tell "price not known yet" from "price is zero",
    // which arithmetic on the total alone cannot express.
    useEffect(() => {
        if (typeof onReady === 'function') onReady(!itemsLoading);
        if (itemsLoading) return;
        if (typeof onTotal === 'function') onTotal(display ? display.total : 0);
    }, [display, onTotal, onReady, itemsLoading]);

    if (itemsLoading) {
        return (
            <div className="bpPreview">
                <div className="bpPreview__title">Booking Cost Preview</div>
                <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '0.88rem' }}>
                    Loading pricing...
                </div>
            </div>
        );
    }

    if (!display || display.items.length === 0) return null;

    const totalBalance = wallet ? parseFloat(wallet.cash_balance) + parseFloat(wallet.bonus_balance) : 0;
    const hasWallet = wallet && totalBalance > 0;
    const hasAnyDiscount = display ? display.items.some(i => i.discount > 0) : false;

    // For edit mode: price difference
    const priceChange = bookingId && originalTotal > 0 ? display.total - originalTotal : null;

    /* THE "not charged yet" NOTE OUTLIVES THE EDITOR (2026-08-30). It used to
       hang off editingPrices, so pressing Done made it vanish — at precisely
       the moment it matters most: the editor is closed, the new numbers are
       sitting there looking settled, and nothing on the panel says the money
       has not moved. draftBackup is the snapshot taken when Edit prices was
       opened, i.e. the last SAVED state, so anything differing from it is
       staged and unsaved. A plain price delta counts too — no override
       needed for the totals to be pending. Not a useMemo: this sits below
       two early returns and hooks cannot. */
    const canon = (o) => JSON.stringify(Object.keys(o || {}).sort().map((k) => [k, o[k]]));
    const overridesStaged = Boolean(draftBackup) && canon(overrides) !== canon(draftBackup);
    const showHeldNote = Boolean(bookingId)
        && (editingPrices || overridesStaged || (priceChange !== null && Math.abs(priceChange) > 0.009));
    const netCharge = bookingId && originalTotal > 0 ? display.total - originalTotal : display.total;
    const balanceAfter = totalBalance - netCharge;
    const isInsufficient = hasWallet && balanceAfter < 0;

    return (
        <div className={`bpPreview${hasAnyDiscount ? '' : ' bpPreview--noDiscount'}${editingPrices ? ' bpPreview--editing' : ''}`}>
            <div className="bpPreview__title bpPreview__titleRow">
                <span>{bookingId ? 'Updated Booking Cost' : 'Booking Cost Preview'}</span>
                {canOverride && onOverridesChange && !editingPrices && (
                    <button
                        type="button"
                        className="bpPreview__editBtn"
                        onClick={() => {
                            setDraftBackup(JSON.parse(JSON.stringify(overrides || {})));
                            setEditingPrices(true);
                            setApplyMsg('');
                        }}
                        title="Override line prices — sent to Zoho as a custom price, never a discount"
                    >
                        ✎ Edit prices
                    </button>
                )}
                {canOverride && editingPrices && (
                    <span className="bpPreview__editActions">
                        <button
                            type="button"
                            className="bpPreview__editBtn"
                            onClick={() => {
                                if (draftBackup && onOverridesChange) onOverridesChange(draftBackup);
                                setEditingPrices(false);
                                setApplyMsg('');
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="bpPreview__editBtn bpPreview__editBtn--primary"
                            onClick={async () => {
                                // NOTHING IS SAVED HERE any more, in either mode.
                                // Done closes the editor and the numbers stay in
                                // the draft; the booking's own Save is what
                                // writes them and settles the money, once
                                // (2026-08-30). Before this, Done wrote straight
                                // to the database and refunded or charged on the
                                // spot — so Cancel could not undo it and a
                                // half-finished edit was already live.
                                setApplyMsg('');
                                if (onApplyOverrides) { try { await onApplyOverrides(); } catch (_) { /* draft only */ } }
                                setEditingPrices(false);
                            }}
                        >
                            Done
                        </button>
                    </span>
                )}
            </div>

            <div className="bpPreview__table">
                <div className="bpPreview__head">
                    <span className="bpPreview__colItem">Item</span>
                    <span className="bpPreview__colRight">Qty</span>
                    <span className="bpPreview__colRight">Unit Price</span>
                    {hasAnyDiscount && <span className="bpPreview__colRight">Discount</span>}
                    <span className="bpPreview__colRight">Subtotal</span>
                    <span className="bpPreview__colRight">VAT</span>
                    <span className="bpPreview__colRight bpPreview__colTotal">Total</span>
                </div>
                {display.items.map((item, idx) => {
                    const d = (overrides && item.itemId != null && overrides[item.itemId]) || { kind: '', value: '', reason: '', qty: '', qtyTouched: false };
                    const setLine = (patch) => onOverridesChange && onOverridesChange({
                        ...(overrides || {}),
                        [item.itemId]: { ...d, ...patch },
                    });
                    return (
                    <React.Fragment key={idx}>
                    <div className="bpPreview__row">
                        <span className="bpPreview__colItem">
                            <span className="bpPreview__itemName">{item.name}</span>
                            {item.tag && <span className={`bpPreview__badge bpPreview__badge--${item.tag.toLowerCase().replace(/[^a-z]/g, '')}`}>{item.tag}</span>}
                        </span>
                        <span className="bpPreview__colRight">
                            {/* QUANTITY IS EDITABLE ON ANY OVERRIDABLE LINE
                                (2026-08-30). An add-on's count writes straight
                                to the form's own state; every other line —
                                the hourly service especially — goes through
                                the override, because its quantity is computed
                                (hours x cleaners) and would otherwise be
                                recalculated away on the next edit. */}
                            {editingPrices && onQuantityChange && item.addonId != null ? (
                                <input
                                    type="number"
                                    className="pop-qty"
                                    min="0.5"
                                    step="0.5"
                                    value={item.quantity}
                                    aria-label={`Quantity — ${item.name}`}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) => onQuantityChange(item.addonId, parseFloat(e.target.value) || 1)}
                                />
                            ) : editingPrices && item.itemId != null && !item.lockedDeposit ? (
                                <span className="pop-qtyCell">
                                {/* The original sits beside the box WHILE you
                                    edit, not only afterwards — changing 9 to 6
                                    and losing sight of the 9 is exactly when
                                    you want it (2026-08-30). */}
                                {item.quantityOverridden && (
                                    <s className="pop-was pop-was--qty">{fmtQty(item.computedQuantity)}</s>
                                )}
                                <input
                                    type="number"
                                    className={`pop-qty${d.qty !== '' && d.qty != null ? ' pop-qty--set' : ''}`}
                                    min="0.5"
                                    step="0.5"
                                    /* ONCE TOUCHED, THE FIELD IS THE DRAFT'S —
                                       including empty. Falling back to the
                                       computed quantity whenever the draft was
                                       blank meant the number reappeared the
                                       instant you deleted it, so it could not be
                                       cleared or retyped (2026-08-30). An empty
                                       box simply means "no override". */
                                    value={d.qtyTouched ? d.qty : item.quantity}
                                    aria-label={`Quantity — ${item.name}`}
                                    title="Set the quantity by hand — it stays put when the hours or cleaners change"
                                    /* Select the whole value on focus. The box
                                       starts life showing the COMPUTED quantity,
                                       so typing a digit appended to it — click
                                       9, type 8, get 98 (2026-08-30). Typing now
                                       replaces, which is the only thing anyone
                                       does here. */
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) => setLine({ qty: e.target.value, qtyTouched: true })}
                                />
                                </span>
                            ) : item.quantityOverridden ? (
                                /* Not being edited, but somebody set this by
                                   hand — show both, the way the unit price
                                   already does (2026-08-30). */
                                <>
                                    <s className="pop-was">{fmtQty(item.computedQuantity)}</s>
                                    {fmtQty(item.quantity)}
                                </>
                            ) : fmtQty(item.quantity)}
                        </span>
                        <span className="bpPreview__colRight">
                            {item.overridden && <s className="pop-was">{money(item.originalUnit)}</s>}
                            {money(item.unitPrice)}
                        </span>
                        {hasAnyDiscount && <span className="bpPreview__colRight">{item.discount > 0 ? `${item.discount}%` : '—'}</span>}
                        <span className="bpPreview__colRight">{money(item.lineSubtotal)}</span>
                        <span className="bpPreview__colRight">
                            {item.taxRate > 0 ? <>{money(item.taxAmount)} <span className="bpPreview__taxRate">(5%)</span></> : '—'}
                        </span>
                        <span className="bpPreview__colRight bpPreview__colTotal">{money(item.lineTotal)}</span>
                    </div>
                    {editingPrices && (
                        <div className="bpPreview__overrideRow">
                            {item.lockedDeposit ? (
                                <span className="pop-locked" title="The deposit is held money, not a fee — it cannot be discounted.">Deposit — locked</span>
                            ) : item.itemId == null ? (
                                <span className="pop-locked">Priced by its own field</span>
                            ) : (
                                <span className="pop-controls">
                                    <span className="pop-kind-select">
                                        <Select
                                            options={OVERRIDE_KIND_OPTIONS}
                                            value={OVERRIDE_KIND_OPTIONS.find((o) => o.value === d.kind) || OVERRIDE_KIND_OPTIONS[0]}
                                            onChange={(opt) => setLine({ kind: opt?.value ?? '', value: '' })}
                                            styles={overrideKindStyles}
                                            isSearchable={false}
                                            menuPlacement="auto"
                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                                        />
                                    </span>
                                    {d.kind && (
                                        <input type="number" className="pop-value" min="0"
                                            max={d.kind === '%' ? 100 : undefined}
                                            step={d.kind === '%' ? 1 : 0.01}
                                            value={d.value}
                                            placeholder={d.kind === '%' ? '0–100' : 'excl. VAT'}
                                            onChange={(e) => setLine({ value: e.target.value })} />
                                    )}
                                    {d.kind && d.value !== '' && (
                                        <input type="text" className="pop-reason" value={d.reason || ''}
                                            placeholder="Reason (optional)"
                                            onChange={(e) => setLine({ reason: e.target.value })} />
                                    )}
                                    <span className="pop-quick">
                                        <button type="button" onClick={() => setLine({ kind: '%', value: '100' })}>Free</button>
                                        <button type="button" onClick={() => setLine({ kind: '%', value: '50' })}>50%</button>
                                        {/* Clear appears for a QUANTITY override too. It only
                                            watched the price fields, so a line whose quantity
                                            had been set by hand offered no way to undo it —
                                            the manager could change the number but never get
                                            back to "no override at all" (2026-08-30). */}
                                        {((d.kind && d.value !== '') || (d.qty !== '' && d.qty != null)) && (
                                            <button type="button" className="pop-clear" onClick={() => setLine({ kind: '', value: '', reason: '', qty: '', qtyTouched: false })}>Clear</button>
                                        )}
                                    </span>
                                </span>
                            )}
                        </div>
                    )}
                    </React.Fragment>
                    );
                })}
            </div>

            <div className="bpPreview__totals">
                <div className="bpPreview__totalRow"><span>Subtotal</span><span>{money(display.subtotal)}</span></div>
                <div className="bpPreview__totalRow"><span>VAT (5%)</span><span>{money(display.vat)}</span></div>
                <div className="bpPreview__totalRow bpPreview__totalRow--grand"><span>Total (incl. VAT)</span><span>{money(display.total)}</span></div>
            </div>

            {applyMsg && (
                <div className="bpPreview__applyRow"><span className="bpPreview__applyMsg">{applyMsg}</span></div>
            )}
            {(editingPrices || showHeldNote) && (
                <div className="bpPreview__applyRow bpPreview__applyRow--held">
                    <span className="bpPreview__applyMsg bpPreview__applyMsg--held">
                        {bookingId
                            ? 'Nothing is charged or refunded yet — press Update Booking to save these.'
                            : 'Applies when the booking is created.'}
                    </span>
                </div>
            )}

            {/* Edit mode: show price change */}
            {bookingId && originalTotal > 0 && priceChange !== null && priceChange !== 0 && (
                <div className={`bpPreview__change ${priceChange > 0 ? 'bpPreview__change--increase' : 'bpPreview__change--decrease'}`}>
                    <div className="bpPreview__changeRow"><span>Previously Charged</span><span>{money(originalTotal)}</span></div>
                    <div className="bpPreview__changeRow"><span>New Total</span><span>{money(display.total)}</span></div>
                    <div className="bpPreview__changeRow bpPreview__changeRow--result">
                        <span>{priceChange > 0 ? 'Additional Charge' : 'Refund'}</span>
                        <span className={priceChange > 0 ? 'bpPreview__changeValue--collect' : 'bpPreview__changeValue--refund'}>
                            {priceChange > 0 ? `+${money(priceChange)}` : `-${money(Math.abs(priceChange))}`}
                        </span>
                    </div>
                </div>
            )}

            {/* Wallet */}
            {showWallet && clientId && (
                <div className={`bpPreview__wallet ${isInsufficient ? 'bpPreview__wallet--warn' : ''}`}>
                    <div className="bpPreview__walletTitle">Client Wallet</div>
                    {walletLoading ? (
                        <div className="bpPreview__walletRow"><span>Loading...</span></div>
                    ) : hasWallet ? (
                        <>
                            <div className="bpPreview__walletRow"><span>Available Balance</span><span className="bpPreview__walletValue">{money(totalBalance)}</span></div>
                            {bookingId && originalTotal > 0 && netCharge === 0 ? (
                                // Edit mode, no fee delta — skip the "+AED 0.00" /
                                // "Balance After" rows (both are redundant) and show
                                // a single "no change" message instead.
                                <div className="bpPreview__walletRow bpPreview__walletRow--balance">
                                    <span style={{ width: '100%', textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>
                                        No change in booking fees
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <div className="bpPreview__walletRow">
                                        <span>{bookingId && originalTotal > 0 ? 'Net Change' : 'This Booking'}</span>
                                        <span className={`bpPreview__walletValue ${netCharge > 0 ? 'bpPreview__walletValue--negative' : 'bpPreview__walletValue--positive'}`}>
                                            {netCharge > 0 ? `-${money(netCharge)}` : `+${money(Math.abs(netCharge))}`}
                                        </span>
                                    </div>
                                    <div className="bpPreview__walletRow bpPreview__walletRow--balance">
                                        <span>Balance After</span>
                                        <span className={`bpPreview__walletValue ${balanceAfter < 0 ? 'bpPreview__walletValue--negative' : ''}`}>{money(balanceAfter)}</span>
                                    </div>
                                    {isInsufficient && (<div className="bpPreview__warning">Insufficient wallet balance — booking will be set to pending</div>)}
                                </>
                            )}
                        </>
                    ) : (
                        <div className="bpPreview__walletRow"><span className="bpPreview__walletMuted">No wallet deposits</span></div>
                    )}
                </div>
            )}
        </div>
    );
}
